<script setup>
import { computed, ref, watch } from 'vue';
import { useCalendarFilterOptions } from '@/js/core/composables/useCalendarFilterOptions';
import { usePastEventsToggle } from '@/js/core/composables/usePastEventsToggle';
import EventViewCalendar from '@/js/core/components/parts/calendar/EventViewCalendar.vue';
import EventViewCategories from '@/js/core/components/parts/calendar/EventViewCategories.vue';
import EventViewList from '@/js/core/components/parts/calendar/EventViewList.vue';
import { listEvents, nextEventsPage } from '@/js/core/utils/events-api';
import { useEventsListStore } from '@/js/core/stores/events-list-store';
import {
  eventFilterGroupsFromOptions,
  sortEventsByStartTime,
} from '@/js/core/utils/calendar-events';
import { calendarFiltersForApi } from '@/js/core/utils/calendar-filter-state';
import {
  EVENT_LIST_CALENDAR_FALLBACK_GROUP,
  eventListCalendarCurrentMonthRange,
  eventListCalendarFilterDefaults,
  eventListCalendarRanges,
  normalizeEventListCalendarConfig,
} from '@/js/core/utils/event-list-calendar';
import { DEFAULT_TIMEZONE, normalizeTimezone } from '@/js/core/utils/timezone';
import CalendarFilters from '@/js/core/components/parts/calendar/CalendarFilters.vue';
import CalendarInfo from '@/js/core/components/parts/calendar/CalendarInfo.vue';
import { audienceGroupLabel } from '@/js/core/utils/audience-group.js';
import { useProfileStore } from '@/js/core/stores/profile-store.js';
import { useUserStore } from '@/js/core/stores/user-store.js';
import { useTimezoneStore } from '@/js/core/stores/timezone-store.js';

const PER_PAGE = 100;
const LAYOUT_TO_VIEW = {
  list: 'list',
  calendar: 'calendar',
  grid: 'categories',
};
const VIEWS = {
  list: EventViewList,
  calendar: EventViewCalendar,
  categories: EventViewCategories,
};

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },
});

const profileStore = useProfileStore();
const userStore = useUserStore();
const timezoneStore = useTimezoneStore();
const eventsStore = useEventsListStore();

const rawEvents = ref([]);
const isLoading = ref(false);
const errorMessage = ref('');
const activeView = ref('list');
const selectedFilters = ref({});
const currentDateRange = ref([eventListCalendarCurrentMonthRange(currentProfileTimezone())]);
let latestRequestKey = '';

const blockConfig = computed(() => normalizeEventListCalendarConfig(props.config));
const timezone = computed(() => currentProfileTimezone());

const timezoneLabelFormatted = computed(() => {
  const label = timezoneStore.effectiveTimezoneLabel || '';

  // Checks if the label already ends with or includes "time" (case-insensitive)
  if (label.toLowerCase().includes('time')) {
    return `${label}.`;
  }

  return `${label} time.`;
});

const selectedMonthRange = computed(() => {
  return blockConfig.value.timeSpan === 'this_month' ? currentDateRange.value[0] : null;
});
const ranges = computed(() =>
  eventListCalendarRanges(
    blockConfig.value.timeSpan,
    activeView.value,
    timezone.value,
    selectedMonthRange.value
  )
);
const displayRange = computed(() => ranges.value.displayRange);
const fetchRange = computed(() => ranges.value.fetchRange);
const filterOptionDateRanges = computed(() => [fetchRange.value]);
const requestKey = computed(() =>
  JSON.stringify({
    fetchRange: fetchRange.value,
    eventGroups: blockConfig.value.eventGroups,
    filters: selectedFilters.value,
  })
);
const { filterGroups, academicYears } = useCalendarFilterOptions(filterOptionDateRanges, {
  groupIds: computed(() => blockConfig.value.eventGroups),
});
const eventFilterGroups = computed(() => eventFilterGroupsFromOptions(filterGroups.value));

const events = computed(() => sortEventsByStartTime(rawEvents.value));

const audienceGroup = computed(() => audienceGroupLabel(profileStore.profile, userStore.isGuest));

const activeViewComponent = computed(() => VIEWS[activeView.value] || EventViewList);

const emptyMessage = computed(() => {
  return {
    today: 'There are no events today.',
    upcoming: 'There are no upcoming events this week.',
    this_month: 'There are no upcoming events this month.',
  }[blockConfig.value.timeSpan];
});

const isThisMonthView = computed(() => blockConfig.value.timeSpan === 'this_month');

// The whole month is already fetched for this time span, so the toggle only
// changes which of the loaded events the grid displays.
const canTogglePastEvents = computed(() => blockConfig.value.timeSpan === 'this_month');
const { showPastEvents, togglePastEvents } = usePastEventsToggle(currentDateRange);

const handleDateChange = range => {
  currentDateRange.value = range;
};

watch(
  () => blockConfig.value.layout,
  layout => {
    activeView.value = LAYOUT_TO_VIEW[layout] || 'list';
  },
  { immediate: true }
);

watch(
  () => blockConfig.value.eventGroups.join('|'),
  () => {
    selectedFilters.value = eventListCalendarFilterDefaults(blockConfig.value.eventGroups);
  },
  { immediate: true }
);

watch(
  timezone,
  displayTimezone => {
    currentDateRange.value = [eventListCalendarCurrentMonthRange(displayTimezone)];
  },
  { immediate: true }
);

watch(
  requestKey,
  async key => {
    latestRequestKey = key;
    isLoading.value = true;
    errorMessage.value = '';

    try {
      const fetchedEvents = await fetchEvents(fetchRange.value);

      if (latestRequestKey !== key) {
        return;
      }

      rawEvents.value = fetchedEvents;
      seedEventsStore(fetchedEvents);
    } catch (error) {
      if (latestRequestKey !== key) {
        return;
      }

      console.error('Event List / Calendar block failed to load events.', error);
      rawEvents.value = [];
      errorMessage.value = 'Could not load events.';
    } finally {
      if (latestRequestKey === key) {
        isLoading.value = false;
      }
    }
  },
  { immediate: true }
);

/**
 * Hand the block's events to the store the event modal reads from.
 *
 * The list and detail endpoints share a presenter, so a card already holds everything the
 * modal renders and it can open on click with no request at all. The one gap is a null
 * description, which only the detail endpoint refills from the oversized raw copy — those
 * rows are flagged for a background refresh.
 */
function seedEventsStore(fetchedEvents) {
  fetchedEvents.forEach(event => {
    eventsStore.setEventFromHydration(event, {
      needsDetailRefresh: event.description === null,
    });
  });
}

async function fetchEvents(range) {
  const allEvents = [];
  let currentPage = 1;

  while (currentPage) {
    const response = await listEvents(
      {
        date_ranges: [range],
        filters: calendarFiltersForApi(selectedFilters.value),
        per_page: PER_PAGE,
        page: currentPage,
      },
      'calendar',
      null
    );

    allEvents.push(...(response.data || []));
    currentPage = nextEventsPage(response.meta?.pagination, currentPage);
  }

  return allEvents;
}

function currentProfileTimezone() {
  const profileTimezone = window.gsdNowAppData?.currentProfile?.timezone || {};

  return normalizeTimezone(profileTimezone.effective_timezone, DEFAULT_TIMEZONE);
}
</script>

<template>
  <div class="b-calendar__events" :data-layout="activeView" :data-range="blockConfig.timeSpan">
    <div class="b-calendar__content">
      <p v-if="isLoading" class="b-calendar__state" aria-live="polite">Loading events...</p>
      <p v-else-if="errorMessage" class="b-calendar__state" role="alert">
        {{ errorMessage }}
      </p>
      <template v-else>
        <CalendarFilters
          v-if="isThisMonthView"
          v-model:view="activeView"
          v-model:filters="selectedFilters"
          :academic-years="academicYears"
          :selected-months="currentDateRange"
          :filter-groups="filterGroups"
          :academic-year-link-visible="false"
          :view-type-visible="isThisMonthView"
          @months-selection-changed="handleDateChange"
        />
        <component
          :is="activeViewComponent"
          :events="events"
          :active-range="displayRange"
          :timezone="timezone"
          :show-past-events="canTogglePastEvents && showPastEvents"
          :can-toggle-past-events="canTogglePastEvents"
          :hide-expired-events="blockConfig.hideExpiredEvents"
          :event-filter-groups="eventFilterGroups"
          :event-filter-fallback-group="EVENT_LIST_CALENDAR_FALLBACK_GROUP"
          :empty-message="emptyMessage"
          event-navigation-mode="link"
          @toggle-past-events="togglePastEvents"
        />
        <CalendarInfo
          :audience-group="audienceGroup"
          :timezone-label-formatted="timezoneLabelFormatted"
          :is-guest="userStore.isGuest"
        />
      </template>
    </div>
  </div>
</template>
